import { useForm, useWatch, Controller } from "react-hook-form";
import SearchSelect from "components/ui/SearchSelect";

const GLOBAL_OPT = { value: "", label: "— Global (no org) —" };
const NO_STYLE_OPT = { value: "", label: "— Org default (any style) —" };

export default function PromptEdit({
  initialName,
  initialDescription,
  initialOrganizationStyleId,
  initialStyleId,
  organizationStyles,
  onSubmit,
}) {
  const showOrgFields = Array.isArray(organizationStyles);

  const orgOptions = [
    GLOBAL_OPT,
    ...(organizationStyles || []).map((o) => ({ value: o._id, label: o.name })),
  ];

  const findOrgOption = (id) =>
    orgOptions.find((o) => o.value === (id || "")) || GLOBAL_OPT;

  const findStyleOption = (orgId, styleId) => {
    const org = (organizationStyles || []).find((o) => o._id === orgId);
    const opt = (org?.styles || []).find((s) => s._id === styleId);
    return opt ? { value: opt._id, label: opt.name } : NO_STYLE_OPT;
  };

  const { register, handleSubmit, control, setValue, formState: { errors } } =
    useForm({
      defaultValues: {
        name: initialName,
        description: initialDescription,
        organizationStyleId: initialOrganizationStyleId || "",
        styleId: initialStyleId || "",
      },
    });

  const selectedOrgId = useWatch({ control, name: "organizationStyleId" });
  const selectedStyleId = useWatch({ control, name: "styleId" });
  const selectedOrg = (organizationStyles || []).find(
    (o) => o._id === selectedOrgId
  );
  const styleOptions = [
    NO_STYLE_OPT,
    ...(selectedOrg?.styles || []).map((s) => ({
      value: s._id,
      label: s.name,
    })),
  ];

  const submitForm = (data) => {
    onSubmit({
      ...data,
      organizationStyleId: data.organizationStyleId || null,
      styleId: data.styleId || null,
    });
  };

  return (
    <form onSubmit={handleSubmit(submitForm)} className="space-y-4 p-2">
      <div className="space-y-2">
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          Name
        </label>
        <input
          type="text"
          id="name"
          {...register("name", { required: true })}
          placeholder="Enter name"
          className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none sm:text-sm"
        />
        {errors.name && <p className="text-red-600 text-sm">Name is required</p>}
      </div>

      {showOrgFields && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Organization
            </label>
            <Controller
              control={control}
              name="organizationStyleId"
              render={({ field }) => (
                <SearchSelect
                  options={orgOptions}
                  value={findOrgOption(field.value)}
                  onChange={(opt) => {
                    field.onChange(opt?.value ?? "");
                    setValue("styleId", "");
                  }}
                  placeholder="Select organization..."
                />
              )}
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Style
            </label>
            <Controller
              control={control}
              name="styleId"
              render={({ field }) => (
                <SearchSelect
                  options={styleOptions}
                  value={findStyleOption(selectedOrgId, selectedStyleId)}
                  onChange={(opt) => field.onChange(opt?.value ?? "")}
                  placeholder="Select style..."
                  disabled={!selectedOrgId}
                />
              )}
            />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
          Description
        </label>
        <textarea
          id="description"
          {...register("description")}
          placeholder="Enter description"
          rows={4}
          className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none sm:text-sm"
        />
      </div>
      <button
        type="submit"
        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        Save
      </button>
    </form>
  );
}
